import { v4 as uuidv4 } from 'uuid';
import { clients } from 'lib/https';
import { ConfigurationSettings } from 'models/configuration-settings';
import { IFileDataWithSharing } from '../utils/file-manager';

/** Id the backend treats as the Media Library root directory. */
export const LIBRARY_ROOT_DIRECTORY_ID = 'libdir_711d51b348cc8191aa5503883a2b5206';

export type LibraryFolder = {
  FileId: string;
  FileName: string;
  parent_directory_id: string;
  itemCount: number;
  createdDate?: string | null;
  lastUpdatedDate?: string | null;
  path?: string | null;
};

export const LIBRARY_FOLDERS_PAGE_SIZE = 10;

export type LibraryFolderListQuery = {
  search?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  order?: string;
  parent_directory_id?: string;
};

export type LibraryFolderListResponse = {
  data: LibraryFolder[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type CreateLibraryFolderPayload = {
  FileId?: string;
  FileName: string;
  parent_directory_id?: string;
};

export type RenameLibraryFolderPayload = {
  FileId: string;
  FileName: string;
};

export type MoveLibraryFilePayload = {
  FileId: string;
  parent_directory_id: string;
};

export type MoveLibraryFileResponse = {
  FileId: string;
  parent_directory_id: string;
};

const foldersUrl = (configurationSettings: ConfigurationSettings) =>
  `${configurationSettings.paths.seliseAiBaseUrl}${configurationSettings.paths.seliseAi.mediaLibraryFolders}`;

const moveFileUrl = (configurationSettings: ConfigurationSettings) =>
  `${configurationSettings.paths.seliseAiBaseUrl}${configurationSettings.paths.seliseAi.mediaLibraryFileMove}`;

const toDate = (value?: string | null, fallback?: string | null) => {
  const raw = value || fallback;
  return raw ? new Date(raw) : new Date();
};

export const mapLibraryFolderToFileData = (folder: LibraryFolder): IFileDataWithSharing => ({
  id: folder.FileId,
  name: folder.FileName,
  fileType: 'Folder',
  size: '0',
  itemCount: folder.itemCount,
  parentDirectoryId: folder.parent_directory_id,
  createdDate: toDate(folder.createdDate),
  lastModified: toDate(folder.lastUpdatedDate, folder.createdDate),
});

export const getLibraryFolders = async (
  configurationSettings: ConfigurationSettings
): Promise<LibraryFolder[]> =>
  clients.get<LibraryFolder[]>(foldersUrl(configurationSettings), configurationSettings.blocksKey);

export const listLibraryFolders = async (
  configurationSettings: ConfigurationSettings,
  query: LibraryFolderListQuery = {}
): Promise<LibraryFolderListResponse> => {
  const body: Record<string, string | number> = {
    page: query.page ?? 1,
    pageSize: query.pageSize ?? LIBRARY_FOLDERS_PAGE_SIZE,
    sortBy: query.sortBy ?? 'fileName',
    order: query.order ?? 'asc',
  };
  const search = query.search?.trim();
  if (search) {
    body.search = search;
  }
  if (query.parent_directory_id) {
    body.parent_directory_id = query.parent_directory_id;
  }

  return clients.post<LibraryFolderListResponse>(
    `${foldersUrl(configurationSettings)}/list`,
    configurationSettings.blocksKey,
    JSON.stringify(body)
  );
};

export const createLibraryFolder = async (
  configurationSettings: ConfigurationSettings,
  payload: CreateLibraryFolderPayload
): Promise<LibraryFolder> =>
  clients.post<LibraryFolder>(
    foldersUrl(configurationSettings),
    configurationSettings.blocksKey,
    JSON.stringify({
      FileId: payload.FileId || uuidv4().replace(/-/g, ''),
      FileName: payload.FileName,
      parent_directory_id: payload.parent_directory_id || LIBRARY_ROOT_DIRECTORY_ID,
    })
  );

export const renameLibraryFolder = async (
  configurationSettings: ConfigurationSettings,
  payload: RenameLibraryFolderPayload
): Promise<LibraryFolder> =>
  clients.put<LibraryFolder>(
    foldersUrl(configurationSettings),
    configurationSettings.blocksKey,
    JSON.stringify(payload)
  );

export const deleteLibraryFolder = async (
  configurationSettings: ConfigurationSettings,
  fileId: string
): Promise<void> => {
  await clients.delete(
    `${foldersUrl(configurationSettings)}/${encodeURIComponent(fileId)}`,
    configurationSettings.blocksKey
  );
};

export const moveLibraryFile = async (
  configurationSettings: ConfigurationSettings,
  payload: MoveLibraryFilePayload
): Promise<MoveLibraryFileResponse> =>
  clients.post<MoveLibraryFileResponse>(
    moveFileUrl(configurationSettings),
    configurationSettings.blocksKey,
    JSON.stringify({
      FileId: payload.FileId,
      parent_directory_id: payload.parent_directory_id,
    })
  );
